import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";
import { FadeUp, Eyebrow, MaskedLines } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { ArrowRight } from "lucide-react";

export default function News() {
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState("All");

  useEffect(() => {
    api.get("/news").then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, []);

  const categories = useMemo(() => ["All", ...new Set(articles.map((a) => a.category))], [articles]);
  const filtered = category === "All" ? articles : articles.filter((a) => a.category === category);
  const featuredArticle = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div data-testid="news-page" className="pt-28 md:pt-36 pb-24">
      <Seo
        path="/news"
        title="News & Insights — Mombasa Property Market"
        description="Market trends, project updates and property insights from SBS Properties — Mombasa's trusted real estate partner since 1952."
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <Eyebrow>News & Insights</Eyebrow>
        <MaskedLines
          className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
          lines={["Market trends &", <em key="e" className="italic text-brand">project updates.</em>]}
        />

        <FadeUp delay={0.15} className="mt-10 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`news-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-[0.15em] font-bold border transition-colors duration-300 ${
                category === c ? "bg-brand text-ivory border-brand" : "border-foreground/15 text-muted-foreground hover:text-foreground hover:border-foreground/40"
              }`}
            >
              {c}
            </button>
          ))}
        </FadeUp>

        {featuredArticle && (
          <FadeUp delay={0.2} className="mt-12">
            <Link
              to={`/news/${featuredArticle.slug}`}
              data-testid="news-featured-card"
              className="group grid grid-cols-1 lg:grid-cols-2 border hairline bg-white text-left overflow-hidden w-full"
            >
              <div className="overflow-hidden">
                <img src={featuredArticle.image} alt={featuredArticle.title} className="w-full object-contain transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-brand font-bold">{featuredArticle.category}</span>
                <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight group-hover:text-brand transition-colors duration-300">
                  {featuredArticle.title}
                </h2>
                <p className="mt-4 text-muted-foreground text-sm leading-relaxed">{featuredArticle.excerpt}</p>
                <span className="mt-6 text-xs text-muted-foreground">
                  {formatDate(featuredArticle.published_at)} · {featuredArticle.read_time} · {featuredArticle.author}
                </span>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand">
                  Read Article <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </FadeUp>
        )}

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rest.map((a, i) => (
            <FadeUp key={a.id} delay={i * 0.08}>
              <Link to={`/news/${a.slug}`} data-testid={`news-card-${i}`} className="group block text-left w-full border hairline bg-white overflow-hidden">
                <div className="overflow-hidden aspect-[16/9]">
                  <img src={a.image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-brand font-bold">{a.category}</span>
                  <h3 className="mt-2 font-serif text-xl leading-snug group-hover:text-brand transition-colors duration-300">{a.title}</h3>
                  <span className="mt-3 block text-xs text-muted-foreground">{formatDate(a.published_at)} · {a.read_time}</span>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="mt-16 text-center text-muted-foreground" data-testid="news-empty">No articles in this category yet.</p>
        )}
      </div>
    </div>
  );
}
